import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  Fade,
  Grow
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';

interface QuickStatProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    period: string;
  };
  delay?: number;
}

const QuickStat: React.FC<QuickStatProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  delay = 0
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend.value > 0) return <TrendingUpIcon fontSize="small" />;
    if (trend.value < 0) return <TrendingDownIcon fontSize="small" />;
    return <TrendingFlatIcon fontSize="small" />;
  };

  const getTrendColor = () => {
    if (!trend) return theme.palette.text.secondary;
    if (trend.value > 0) return theme.palette.success.main;
    if (trend.value < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  return (
    <Grow in timeout={800 + delay}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.03)} 100%)`,
          border: `1px solid ${alpha(color, 0.15)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${alpha(color, 0.2)}`,
            borderColor: alpha(color, 0.3)
          },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
            zIndex: 0
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Icon and Value */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha(color, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color
              }}
            >
              {icon}
            </Box>

            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: getTrendColor(),
                  bgcolor: alpha(getTrendColor(), 0.1),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                {getTrendIcon()}
                <Typography variant="caption" fontWeight="bold">
                  {Math.abs(trend.value)}%
                </Typography>
              </Box>
            )}
          </Box>

          {/* Value */}
          <Typography
            variant="h4"
            fontWeight="bold"
            color="text.primary"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
          >
            {typeof value === 'number' ? value.toLocaleString('ar-EG') : value}
          </Typography>

          {/* Title */}
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>

          {/* Trend description */}
          {trend && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {trend.period}
            </Typography>
          )}
        </Box>
      </Paper>
    </Grow>
  );
};

interface QuickStatsProps {
  stats: {
    customers: number;
    maintenance: number;
    sales: number;
    inventory: number;
    lowStock: number;
    completed: number;
  };
  trends?: {
    customers: number;
    maintenance: number;
    sales: number;
    inventory: number;
  };
}

const QuickStats: React.FC<QuickStatsProps> = ({ stats, trends }) => {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();

  const statsConfig = [
    {
      title: 'إجمالي العملاء',
      value: stats.customers,
      icon: '👥',
      color: theme.palette.primary.main,
      trend: trends ? { value: trends.customers, period: 'مقارنة بالشهر الماضي' } : undefined
    },
    {
      title: 'أجهزة قيد الصيانة',
      value: stats.maintenance,
      icon: '🔧',
      color: theme.palette.warning.main,
      trend: trends ? { value: trends.maintenance, period: 'مقارنة بالأسبوع الماضي' } : undefined
    },
    {
      title: 'مبيعات اليوم',
      value: formatCurrency(stats.sales),
      icon: '💰',
      color: theme.palette.success.main,
      trend: trends ? { value: trends.sales, period: 'مقارنة بالأمس' } : undefined
    },
    {
      title: 'إجمالي المخزون',
      value: stats.inventory,
      icon: '📦',
      color: theme.palette.info.main,
      trend: trends ? { value: trends.inventory, period: 'مقارنة بالشهر الماضي' } : undefined
    },
    {
      title: 'مخزون منخفض',
      value: stats.lowStock,
      icon: '⚠️',
      color: theme.palette.error.main,
      trend: { value: 0, period: 'يحتاج متابعة' }
    },
    {
      title: 'مكتمل اليوم',
      value: stats.completed,
      icon: '✅',
      color: theme.palette.success.main,
      trend: { value: 15, period: 'مقارنة بالأمس' }
    }
  ];

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          الإحصائيات السريعة
        </Typography>
        
        <Grid container spacing={3}>
          {statsConfig.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={stat.title}>
              <QuickStat
                title={stat.title}
                value={stat.value}
                icon={<span style={{ fontSize: '1.5rem' }}>{stat.icon}</span>}
                color={stat.color}
                trend={stat.trend}
                delay={index * 100}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Fade>
  );
};

export default QuickStats;
